import {
  CaptureType,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  MIN_FILE_SIZE,
} from "types";

export const MAX_PCR_ZIP_SIZE = 2 * 1024 * 1024 * 1024;
export const REQUIRED_IRIS_IMAGE_COUNT = 14;

export interface CaptureFileRequirement {
  required: number;
  maximum?: number;
}

export function getCaptureFileRequirement(
  captureType: CaptureType
): CaptureFileRequirement {
  if (captureType === "iris") {
    return {
      required: REQUIRED_IRIS_IMAGE_COUNT,
      maximum: REQUIRED_IRIS_IMAGE_COUNT,
    };
  }
  if (
    captureType === "file" ||
    captureType === "pcr" ||
    captureType === "video"
  ) {
    return { required: 1 };
  }
  return { required: 1, maximum: 1 };
}

export type UploadFileValidationError =
  | "small size"
  | "large size"
  | "pcr file type"
  | "video file type";

const acceptedFileTypes: Record<CaptureType, string> = {
  photo: "image/*",
  iris: "image/jpeg,image/png,image/webp,image/heic,image/heif,image/x-adobe-dng",
  pcr: "image/*,video/mp4,video/quicktime,application/zip,.zip,application/x-zip-compressed",
  file: "image/*,image/x-adobe-dng,video/mp4,video/quicktime,application/zip,.zip,application/x-zip-compressed",
  orb: "",
  rtms: "",
  video: "video/mp4,video/quicktime,video/3gpp",
};

export function isZipFile(file: Pick<File, "name" | "type">): boolean {
  const type = file.type.toLowerCase();
  return (
    type === "application/zip" ||
    type === "application/x-zip-compressed" ||
    file.name.toLowerCase().endsWith(".zip")
  );
}

export function getAcceptedFileTypes(captureType?: CaptureType): string {
  return acceptedFileTypes[captureType || "photo"];
}

export function validateUploadFile(
  file: File,
  captureType?: CaptureType
): UploadFileValidationError | null {
  if (file.size < MIN_FILE_SIZE) return "small size";

  if (captureType === "pcr") {
    if (!isZipFile(file)) return "pcr file type";
    return file.size > MAX_PCR_ZIP_SIZE ? "large size" : null;
  }

  if (isZipFile(file) && file.size > MAX_VIDEO_SIZE) return "large size";
  if (captureType === "video" && !file.type.startsWith("video/")) {
    return "video file type";
  }
  if (file.type.startsWith("image") && file.size > MAX_IMAGE_SIZE) {
    return "large size";
  }
  if (file.type.startsWith("video") && file.size > MAX_VIDEO_SIZE) {
    return "large size";
  }
  return null;
}

export function getUploadFileValidationMessage(
  file: File,
  captureType?: CaptureType
): string | null {
  const error = validateUploadFile(file, captureType);
  if (error === "small size") return "File is too small, choose another one";
  if (error === "pcr file type") {
    return "Only ZIP files are accepted for PCR uploads";
  }
  if (error === "video file type") {
    return "Only video files are accepted for this task";
  }
  if (error === "large size") {
    if (captureType === "pcr")
      return "ZIP file is too large, choose another one";
    if (isZipFile(file)) {
      return "ZIP file is too large, choose another one";
    }
    if (file.type.startsWith("image")) {
      return "Image is too large, choose another one";
    }
    if (file.type.startsWith("video")) {
      return "Video is too large, choose another one";
    }
  }
  return null;
}
